import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Container, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { renderProfileImage } from '../../utils/imageHelpers';
import { getAllShops, getShopProducts, convertShopNameToUrl } from '../../services/shopService';
import useDebounce from '../../hooks/useDebounce';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getUserProfile } from '../../services/userService';
import SearchBar from '../../components/SearchBar';
// Importo l'immagine locale
import nzImage from '../../assets/img/nz.png';

const HomePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser, loading: authLoading } = useAuth();
  const [userProfile, setUserProfile] = useState(null);
  const [featuredShops, setFeaturedShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const { debouncedValue: debouncedSearchQuery, isDebouncing } = useDebounce(searchQuery, 300);
  useEffect(() => {
    const loadUserProfile = async () => {
      if (currentUser) {
        try {
          const profile = await getUserProfile(currentUser.uid);
          setUserProfile(profile);
        } catch (err) {
          console.error('Errore nel caricamento del profilo:', err);
        }
      }
    };

    loadUserProfile();
  }, [currentUser]);

  useEffect(() => {
    const loadShops = async () => {
      try {
        setLoading(true);
        const shops = await getAllShops();
        
        // Per ogni negozio, carichiamo anche i prodotti
        const shopsWithProducts = await Promise.all(
          shops.map(async (shop) => {
            const products = await getShopProducts(shop.id, false);
            return {
              ...shop,
              allProducts: products,
              newProducts: products.slice(0, 3) // Mostriamo i primi 3 prodotti
            };
          })
        );
        
        setFeaturedShops(shopsWithProducts);
      } catch (err) {
        console.error('Errore nel caricamento dei negozi:', err);
        setError('Errore nel caricamento dei dati');
      } finally {
        setLoading(false);
      }
    };

    loadShops();
  }, []);

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  // Determina cosa mostrare: filtra client-side i negozi già caricati
  const isSearching = debouncedSearchQuery.trim() !== '';

  const shopsToDisplay = isSearching
    ? (() => {
        const q = debouncedSearchQuery.toLowerCase();
        return featuredShops
          .map(shop => {
            const shopNameMatch = shop.name.toLowerCase().includes(q);
            const matchingProducts = (shop.allProducts || []).filter(p =>
              p.name.toLowerCase().includes(q)
            );
            if (shopNameMatch || matchingProducts.length > 0) {
              return { ...shop, products: matchingProducts };
            }
            return null;
          })
          .filter(Boolean);
      })()
    : featuredShops;

  if (loading || authLoading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status" />
      </div>
    );
  }

  if (error) {
    return <div className="text-center py-5 text-danger">{error}</div>;
  }

  // Render di un singolo shop card
  const renderShopCard = (shop) => {
    const products = shop.products || shop.newProducts || [];
    const displayProducts = isSearching ? products.slice(0, 6) : products.slice(0, 3);

    return (
      <Card
        key={shop.id}
        className="mb-4 rounded-4 shop-card cursor-pointer"
        onClick={() => navigate(`/${convertShopNameToUrl(shop.name)}`)}
      >
        <div className="shop-rating-badge position-absolute top-0 end-0 m-2">
          <div className="rating-badge d-flex align-items-center">
            <i className="bi bi-star-fill text-warning me-1"></i>
            <span className="fw-bold">{shop.rating}</span>
          </div>
        </div>
        <Card.Body className="p-3">
          <Row className="align-items-center mb-2">
            <Col xs={2} className="text-center">
              <div className="rounded-circle overflow-hidden bg-white mx-auto" style={{ width: '60px', height: '60px' }}>
                <img
                  src={shop.logoUrl}
                  alt={shop.name}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center'
                  }}
                />
              </div>
            </Col>
            <Col xs={7} className="ps-3">
              <Card.Title className="fs-5 mb-0 text-dark">{shop.name}</Card.Title>
              <Card.Text className="text-muted mb-0">
                {shop.subtitle || 'Nessuna descrizione'}
              </Card.Text>
            </Col>
          </Row>

          {displayProducts.length > 0 && (
            <div>
              <p className="mb-2 mt-3 fw-bold">{isSearching ? 'Prodotti trovati' : 'Nuovi Prodotti'}</p>
              <Row className="g-2">
                {displayProducts.map(product => (
                  <Col xs={4} key={product.id}>
                    <Card
                      className="rounded-3 overflow-hidden product-card cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/${convertShopNameToUrl(shop.name)}/product/${product.id}`);
                      }}
                    >
                      <div className="overflow-hidden">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="product-img w-100"
                        />
                      </div>
                      <Card.Body className="p-2 text-center" style={{ backgroundColor: "#F8F8F8" }}>
                        <p className="mb-0 product-title text-dark">{product.name}</p>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>
          )}
        </Card.Body>
      </Card>
    );
  };

  return (
    <div className="fade-in">
      {/* Header con sfondo blu */}
      <div className="header-section">
        <Row className="align-items-center g-0">
          <Col xs={3} className="d-flex justify-content-center">
              {currentUser && userProfile && userProfile.avatar ? (
                renderProfileImage(userProfile.avatar, userProfile.name || currentUser.email, 80)
              ) : (
                <div className="rounded-circle overflow-hidden bg-white d-flex align-items-center justify-content-center" style={{ width: 80, height: 80 }}>
                  <i className="bi bi-person-circle" style={{ fontSize: 80, color: '#e0e0e0' }}></i>
                </div>
              )}
          </Col>
          <Col xs={9}>
            {currentUser && userProfile ? (
              <h2 className="mb-0 fw-bold text-dark">Ciao, {userProfile.name || currentUser.email}</h2>
            ) : (
               <h2 className="mb-0 fw-bold text-dark">Ciao!</h2>
            )}
            <p className="mb-0 text-dark">Benvenuto su hop</p>
          </Col>
        </Row>
        
        {/* Barra di ricerca */}
        <div className="mt-4">
          <SearchBar
            placeholder={t('search.products_shops')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onSearch={handleSearch}
          />
        </div>
      </div>

      <Container fluid className="px-0">
        {/* Banner promozionale secondo le specifiche Figma */}
        <div className="figma-banner">
          <div className="figma-banner__background"></div>
          <div className="figma-banner__decoration"></div>
          <div className="figma-banner__text">
            <h3 className="fw-bold mb-0" style={{ fontSize: '1.4rem' }}>Scopri il meglio.</h3>
            <h3 className="fw-bold" style={{ fontSize: '1.4rem' }}>Ogni giorno su hop</h3>
          </div>
          <img 
            src={nzImage} 
            alt="Avatar personaggio" 
            className="figma-banner__image"
          />
        </div>

        {/* Negozi in evidenza / Risultati ricerca */}
        <div className="shop-container">
          <h4 className="fw-bold mb-3" style={{ fontSize: '1.4rem' }}>
            {isSearching ? 'Risultati della ricerca' : 'Negozio in evidenza'}
          </h4>

          {isDebouncing && (
            <div className="text-center py-3">
              <Spinner animation="border" size="sm" className="me-2" />
              <span className="text-muted">Ricerca in corso...</span>
            </div>
          )}

          {!isDebouncing && shopsToDisplay && shopsToDisplay.map(shop => renderShopCard(shop))}

          {!isDebouncing && isSearching && shopsToDisplay && shopsToDisplay.length === 0 && (
            <div className="text-center py-5">
              <p className="text-muted">{t('search.no_results') || 'Nessun negozio o prodotto trovato'}</p>
            </div>
          )}
        </div>
      </Container>
    </div>
  );
};

export default HomePage; 